<template>
  <div class="lang-dropdown lang-combobox" ref="rootRef">
    <!-- Trigger: shows the active locale's endonym + flag (same collapsed
         footprint as the old dropdown). Clicking opens the search panel. -->
    <button
      type="button"
      class="lang-dropdown-trigger"
      @click="toggle"
      :aria-expanded="open"
      aria-haspopup="listbox"
      aria-label="Language"
    >
      <span class="lang-dropdown-label">{{ localeEndonyms[currentLocale] }}</span>
      <span class="lang-dropdown-flag">{{ localeFlags[currentLocale] }}</span>
      <svg class="lang-dropdown-caret" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>

    <!-- Search panel: text input + filterable listbox. -->
    <div v-if="open" class="lang-dropdown-menu lang-combobox-panel" role="presentation">
      <div class="lang-combobox-search">
        <svg class="lang-combobox-search-icon" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref="inputRef"
          v-model="query"
          type="text"
          class="lang-combobox-input"
          role="combobox"
          aria-label="Search language"
          aria-autocomplete="list"
          :aria-expanded="open"
          :aria-controls="listId"
          :aria-activedescendant="activeId"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          @keydown="onKeydown"
        />
      </div>

      <ul class="lang-combobox-list" :id="listId" role="listbox">
        <li
          v-for="(entry, idx) in filtered"
          :key="entry.code"
          :id="optionId(entry.code)"
          role="option"
          :aria-selected="currentLocale === entry.code"
        >
          <button
            type="button"
            class="lang-option"
            :class="{
              'lang-option-active': currentLocale === entry.code,
              'lang-option-highlight': idx === activeIndex,
            }"
            @click="select(entry.code)"
            @mousemove="activeIndex = idx"
          >
            <span class="lang-option-flag">{{ entry.flag }}</span>
            <span class="lang-option-name">{{ entry.endonym }}</span>
            <span class="lang-option-english">{{ entry.englishName }}</span>
          </button>
        </li>
        <li
          v-if="filtered.length === 0"
          class="lang-combobox-empty"
          role="option"
          aria-disabled="true"
        >
          No results
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { localePath } from '@/router/localePath';
import { supportedLocales, type SupportedLocale } from '@/i18n';
import { localeEndonyms, localeFlags, englishNameOf } from '@/i18n/localeDisplay';
import { trackEvent } from '@/utils/analytics';

interface LangEntry {
  code: SupportedLocale;
  endonym: string;
  flag: string;
  englishName: string;
}

const { locale } = useI18n();
const route = useRoute();
const router = useRouter();

const open = ref(false);
const query = ref('');
const activeIndex = ref(0);
const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

// Stable ids for ARIA wiring (aria-controls / aria-activedescendant).
const listId = 'lang-combobox-list';
const optionId = (code: string) => `lang-combobox-opt-${code}`;

const currentLocale = computed(() => locale.value as SupportedLocale);

// DATA-DRIVEN: the language list is derived from the canonical supportedLocales
// (sourced from @/i18n) joined with the shared endonym/flag/English-name maps.
// Nothing here enumerates a fixed language set, so any locale added in the
// future is included automatically (FR-006).
const entries = computed<LangEntry[]>(() =>
  supportedLocales.map((code) => ({
    code,
    endonym: localeEndonyms[code],
    flag: localeFlags[code],
    englishName: englishNameOf(code),
  })),
);

// Normalize for comparison: NFC + lower-case. toLowerCase() is a no-op for the
// non-Latin scripts (Cyrillic/Hebrew/CJK) but harmless, so one routine covers
// every locale (NFR-001).
const norm = (s: string) => s.normalize('NFC').toLowerCase();

// One prefix-match comparison over the three searchable fields — endonym,
// English name, ISO code — so "бъ" OR "bg" both resolve to Български
// (FR-002 / FR-003 / FR-004).
const matches = (entry: LangEntry, q: string): boolean => {
  const needle = norm(q.trim());
  if (!needle) return true;
  return (
    norm(entry.endonym).startsWith(needle) ||
    norm(entry.englishName).startsWith(needle) ||
    norm(entry.code).startsWith(needle)
  );
};

const filtered = computed<LangEntry[]>(() =>
  entries.value.filter((entry) => matches(entry, query.value)),
);

const activeId = computed(() =>
  filtered.value.length > 0 && filtered.value[activeIndex.value]
    ? optionId(filtered.value[activeIndex.value].code)
    : undefined,
);

// Keep the highlighted row valid as the filter narrows, and scroll it into view.
watch(filtered, () => {
  if (activeIndex.value > filtered.value.length - 1) activeIndex.value = 0;
});

const scrollActiveIntoView = () => {
  const entry = filtered.value[activeIndex.value];
  if (!entry) return;
  const el = document.getElementById(optionId(entry.code));
  el?.scrollIntoView({ block: 'nearest' });
};

const openMenu = async () => {
  open.value = true;
  query.value = '';
  // Position the highlight on the active locale if it is in view, else the top.
  const idx = filtered.value.findIndex((e) => e.code === currentLocale.value);
  activeIndex.value = idx >= 0 ? idx : 0;
  await nextTick();
  inputRef.value?.focus();
  scrollActiveIntoView();
};

const close = () => {
  open.value = false;
  query.value = '';
};

const toggle = () => {
  if (open.value) close();
  else void openMenu();
};

// Selection re-uses the exact action of the prior dropdown: flip the i18n
// locale, persist the ISO code, and fire the GA language-switch event (no-op
// until consent — spec 049-google-analytics). (FR-005)
const select = (code: SupportedLocale) => {
  trackEvent('change_locale', { locale_code: code });
  close();
  // FR-013: rewrite ONLY the :locale segment of the current URL, preserving the
  // rest of the path + query + hash. The router's beforeEach guard (WE-1) then
  // activates the locale — sets i18n, persists cpt-locale and applies dir for
  // RTL — so the URL prefix stays the single source of truth for the switch.
  const current = route?.fullPath;
  const target = current ? localePath(current, code) : null;
  if (router && target && target !== current) {
    void router.push(target);
    return;
  }
  // No router context (e.g. an isolated component test) or already on this
  // locale's URL: set i18n + persist directly so the switch still takes effect.
  locale.value = code;
  localStorage.setItem('cpt-locale', code);
};

const move = (delta: number) => {
  const n = filtered.value.length;
  if (n === 0) return;
  activeIndex.value = (activeIndex.value + delta + n) % n;
  void nextTick(scrollActiveIntoView);
};

const onKeydown = (e: KeyboardEvent) => {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      move(1);
      break;
    case 'ArrowUp':
      e.preventDefault();
      move(-1);
      break;
    case 'Home':
      e.preventDefault();
      activeIndex.value = 0;
      void nextTick(scrollActiveIntoView);
      break;
    case 'End':
      e.preventDefault();
      activeIndex.value = Math.max(0, filtered.value.length - 1);
      void nextTick(scrollActiveIntoView);
      break;
    case 'Enter': {
      e.preventDefault();
      const entry = filtered.value[activeIndex.value];
      if (entry) select(entry.code);
      break;
    }
    case 'Escape':
      e.preventDefault();
      close();
      break;
    default:
      break;
  }
};

// Reset the highlight to the top whenever the query changes (typing).
watch(query, () => {
  activeIndex.value = 0;
});

// Close on outside-click or a global Escape (e.g. focus outside the input).
const onDocumentClick = (e: MouseEvent) => {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target as Node)) {
    close();
  }
};
const onDocumentKeydown = (e: KeyboardEvent) => {
  if (open.value && e.key === 'Escape') close();
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
});
</script>

<style scoped>
/* --- Language combobox (one control, desktop + mobile). Mirrors the old
   .lang-dropdown glassmorphism styling so the header footprint is unchanged. --- */
.lang-dropdown {
  position: relative;
}

.lang-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  /* Mobile-first: 44px touch target for the language selector (spec 026, FR-003). */
  min-height: 44px;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: var(--radius-lg, 0.5rem);
  border: 1px solid rgb(255 255 255 / 0.25);
  background: transparent;
  color: rgb(var(--color-foreground-light));
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--font-weight-medium, 500);
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.lang-dropdown-trigger:hover {
  background: rgb(255 255 255 / 0.1);
  border-color: rgb(255 255 255 / 0.45);
}

.lang-dropdown-label {
  white-space: nowrap;
}
.lang-dropdown-flag {
  font-size: 1.05em;
  line-height: 1;
}
.lang-dropdown-caret {
  transition: transform 0.15s ease;
}
.lang-dropdown-trigger[aria-expanded='true'] .lang-dropdown-caret {
  transform: rotate(180deg);
}

.lang-dropdown-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  min-width: 14rem;
  max-width: min(18rem, calc(100vw - 1.5rem));
  margin: 0;
  padding: 0.4rem;
  list-style: none;
  background: rgb(var(--color-primary));
  border: 1px solid rgb(255 255 255 / 0.18);
  border-radius: var(--radius-lg, 0.5rem);
  box-shadow: 0 10px 30px rgb(0 0 0 / 0.35);
  z-index: 100;
}

/* Search input row */
.lang-combobox-search {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0.4rem;
}
.lang-combobox-search-icon {
  position: absolute;
  left: 0.6rem;
  color: rgb(255 255 255 / 0.55);
  pointer-events: none;
}
.lang-combobox-input {
  width: 100%;
  min-height: 40px;
  padding: 0.45rem 0.6rem 0.45rem 2rem;
  border-radius: var(--radius-md, 0.375rem);
  border: 1px solid rgb(255 255 255 / 0.22);
  background: rgb(255 255 255 / 0.08);
  color: rgb(var(--color-foreground-light));
  font-size: var(--font-size-sm, 0.875rem);
  line-height: 1.2;
  outline: none;
}
.lang-combobox-input:focus {
  border-color: rgb(255 255 255 / 0.5);
  background: rgb(255 255 255 / 0.12);
}
.lang-combobox-input::placeholder {
  color: rgb(255 255 255 / 0.45);
}

/* Options list */
.lang-combobox-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: min(60vh, 20rem);
  overflow-y: auto;
}
.lang-combobox-list li {
  margin: 0;
}

/* Full-width option row: flag, endonym, then muted English name. Natural case
   (overrides the global uppercase .lang-option pill). */
.lang-combobox-list .lang-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  /* 44px touch target on each row (spec 026). */
  min-height: 44px;
  padding: 0.5rem 0.65rem;
  border-radius: var(--radius-md, 0.375rem);
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--font-weight-medium, 500);
  letter-spacing: normal;
  text-transform: none;
  color: rgb(var(--color-foreground-light));
  background: transparent;
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.lang-combobox-list .lang-option:hover,
.lang-combobox-list .lang-option-highlight {
  background: rgb(255 255 255 / 0.1);
}
.lang-combobox-list .lang-option-active {
  background: rgb(var(--color-foreground-light));
  color: rgb(var(--color-primary));
  font-weight: var(--font-weight-bold, 700);
}
.lang-combobox-list .lang-option-active.lang-option-highlight {
  background: rgb(var(--color-foreground-light));
}
.lang-option-flag {
  font-size: 1.15em;
  line-height: 1;
  flex-shrink: 0;
}
.lang-option-name {
  white-space: nowrap;
}
.lang-option-english {
  margin-left: auto;
  padding-left: 0.5rem;
  font-size: 0.78em;
  font-weight: var(--font-weight-normal, 400);
  opacity: 0.55;
  white-space: nowrap;
}
.lang-option-active .lang-option-english {
  opacity: 0.7;
}

.lang-combobox-empty {
  padding: 0.7rem 0.65rem;
  font-size: var(--font-size-sm, 0.875rem);
  color: rgb(255 255 255 / 0.6);
  text-align: center;
}
</style>
